import React, { useEffect, useState } from "react";
import { TravelClientsStyled } from "./travel-clients.styles";
import { Box, Typography } from "@mui/material";
import { Button } from "../../../../components/button/button";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getAllTravelClients, attachTravelClient, getClients } from "../../../../actions";
import Loader from "../../../../components/loader/loader";
import useDebouncedCallback from "../../../../hooks/useDebouncedCallback";
import AutocompleteFormField from "../../../../components/form-fields/autocomplete-field/autocomplete-form-field";

interface ClientData {
  integration_id: string;
  first_name: string;
  last_name: string;
}

interface FormData {
  client_id: string | null;
  travel_client_id: string | null;
  client_search: string;
  travel_client_search: string;
}

interface TravelClientPair {
  id: number;
  integration_id: string;
  first_name: string | null;
  last_name: string | null;
  travel_client: { first_name: string; last_name: string } | null;
}

const TravelClients: React.FC = () => {
  const [pairs, setPairs] = useState<TravelClientPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // For autocomplete options
  const [clients, setClients] = useState<ClientData[]>([]);
  const [travelClients, setTravelClients] = useState<ClientData[]>([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [travelClientSearchLoading, setTravelClientSearchLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const [selectedTravelClient, setSelectedTravelClient] = useState<ClientData | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid, isDirty },
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(
      require('yup').object().shape({
        client_id: require('yup').string().typeError("Client is required").required("Client is required").nullable(),
        travel_client_id: require('yup').string().typeError("Travel Client is required").required("Travel Client is required").nullable(),
        client_search: require('yup').string(),
        travel_client_search: require('yup').string(),
      })
    ),
    mode: "all",
    defaultValues: { client_id: null, travel_client_id: null, client_search: "", travel_client_search: "" },
  });

  useEffect(() => {
    setLoading(true);
    getAllTravelClients()
      .then((res: any) => setPairs(res?.data?.data || []))
      .catch(() => setPairs([]))
      .finally(() => setLoading(false));
  }, []);

  // Debounced search handlers
  const handleClientSearch = useDebouncedCallback((search: string) => {
    setClientSearchLoading(true);
    getClients(search || "")
      .then((response: any) => {
        const { data } = response.data;
        setClients(data);
      })
      .catch(() => setClients([]))
      .finally(() => setClientSearchLoading(false));
  }, 300);

  const handleTravelClientSearch = useDebouncedCallback((search: string) => {
    setTravelClientSearchLoading(true);
    getClients(search || "")
      .then((response: any) => {
        const { data } = response.data;
        setTravelClients(data);
      })
      .catch(() => setTravelClients([]))
      .finally(() => setTravelClientSearchLoading(false));
  }, 300);

  // Watch search fields and trigger search
  useEffect(() => {
    const search = watch("client_search");
    if (search?.trim()) handleClientSearch(search);
    else setClients([]);
  }, [watch("client_search")]);

  useEffect(() => {
    const search = watch("travel_client_search");
    if (search?.trim()) handleTravelClientSearch(search);
    else setTravelClients([]);
  }, [watch("travel_client_search")]);

  // Set selected client/travel client
  const handleClientChange = (_: any, value: { id: string; label: string } | null) => {
    const client = clients.find((c) => c.integration_id === value?.id) || null;
    setSelectedClient(client);
    setValue("client_id", client ? client.integration_id : null, { shouldValidate: true, shouldDirty: true });
    setValue("client_search", client ? `${client.first_name} ${client.last_name}` : "", { shouldValidate: true, shouldDirty: true });
  };

  const handleTravelClientChange = (_: any, value: { id: string; label: string } | null) => {
    const client = travelClients.find((c) => c.integration_id === value?.id) || null;
    setSelectedTravelClient(client);
    setValue("travel_client_id", client ? client.integration_id : null, { shouldValidate: true, shouldDirty: true });
    setValue("travel_client_search", client ? `${client.first_name} ${client.last_name}` : "", { shouldValidate: true, shouldDirty: true });
  };

  const onSubmit = async (data: FormData) => {
    if (!data.client_id || !data.travel_client_id) return;
    setSubmitting(true);
    try {
      await attachTravelClient({
        client_id: data.client_id,
        travel_client_id: data.travel_client_id,
      });
      reset();
      setLoading(true);
      const res = await getAllTravelClients();
      setPairs(res?.data?.data || []);
    } catch {
      // Optionally handle error
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  if (loading) return <Loader absolute />;
  return (
    <TravelClientsStyled>
      <Box className="header">
        <Typography variant="h2">Travel Clients</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Link far away clients to a travel client for improved scheduling. Use the fields below to view or create pairs.
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 320, overflow: "auto", mb: 3 }}>
        {pairs.length === 0 && (
          <Typography>No travel client pairs found.</Typography>
        )}
        {/* Box for adding a new pair */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              background: "#f9f9f9",
              borderRadius: "12px",
              boxShadow: "0 0 4px #f0f0f066",
              p: 2,
              mt: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <AutocompleteFormField
                name="client_search"
                control={control}
                label="Client"
                placeholder="Search client name/phone number here"
                onSelect={handleClientChange}
                options={clients.map((client, index) => ({
                  id: client.integration_id,
                  label: `${index + 1}. ${client.first_name} ${client.last_name}`.trim(),
                }))}
                value={watch("client_search")}
                error={errors.client_id}
                helperText={errors.client_id?.message}
              />
              {clientSearchLoading && <Loader />}
            </Box>
            <Box sx={{ flex: 1 }}>
              <AutocompleteFormField
                name="travel_client_search"
                control={control}
                label="Travel Client"
                placeholder="Search client name/phone number here"
                onSelect={handleTravelClientChange}
                options={travelClients.map((client, index) => ({
                  id: client.integration_id,
                  label: `${index + 1}. ${client.first_name} ${client.last_name}`.trim(),
                }))}
                value={watch("travel_client_search")}
                error={errors.travel_client_id}
                helperText={errors.travel_client_id?.message}
              />
              {travelClientSearchLoading && <Loader />}
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                !isValid ||
                !isDirty ||
                submitting ||
                !watch("client_id") ||
                !watch("travel_client_id")
              }
              isLoading={submitting}
            >
              Attach
            </Button>
          </Box>
        </form>
        {pairs.map((pair) => (
          <Box
            key={pair.id}
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 0 4px #f0f0f066",
              p: 2,
              mb: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Client</Typography>
              <Box sx={{ mt: 1, mb: 1 }}>
                {pair.first_name && pair.last_name
                  ? `${pair.first_name} ${pair.last_name}`
                  : ""}
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Travel Client</Typography>
              <Box sx={{ mt: 1, mb: 1 }}>
                {pair.travel_client
                  ? `${pair.travel_client.first_name} ${pair.travel_client.last_name}`
                  : ""}
              </Box>
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={async () => {
                await attachTravelClient({
                  client_id: pair.integration_id,
                  travel_client_id: null,
                });
                // Refresh list
                setLoading(true);
                const res = await getAllTravelClients();
                setPairs(res?.data?.data || []);
                setLoading(false);
              }}
            >
              Remove
            </Button>
          </Box>
        ))}
      </Box>
    </TravelClientsStyled>
  );
};

export default TravelClients;